import catalog from "./test-ids.json";

/**
 * Stable `data-testid` values — a public API for resilient Playwright
 * `getByTestId` locators on flow-critical paths (finder, results, slot cards,
 * booking wizard, login, events).
 *
 * **Single source of truth: `test-ids.json`.** It is read four ways so a value
 * lives in exactly one place:
 * - here, frozen as `TestId`, for Vue / custom elements (`:data-testid="TestId.booking.next"`);
 * - in Ruby via `TestIds` / the `test_id` view helper, for server-rendered ERB;
 * - via the booking-wizard config (`AppointmentBooking.config.test_ids` → the JST `testIds` context) for the legacy Backbone templates;
 * - synced into the QA suite (combinaut/sparkle-qa-automation) as the consumer.
 *
 * Contract: deprecate, never delete. Removing or renaming a value breaks the
 * consumer — mark a retired entry `@deprecated` and keep it until the QA suite
 * drops its use.
 *
 * Playwright pierces open shadow DOM, so values inside custom-element shadow
 * roots are reachable.
 *
 * Which controls get a hook: **every** flow-critical interactive element — the
 * QA suite targets exclusively via `getByTestId`, no `name=`/`value=`/CSS/text
 * (roles, labels, and text vary per client + locale, so those aren't reliable).
 * Give a deliberately-selected control its own key (`booking.patientNew`,
 * `booking.visitType`); a set of dynamic, arbitrary options (decision-tree
 * question answers) may share one key (`booking.choice`) — drive with `.first()`.
 * Never hardcode a testid on a generic shared component — thread a prop from the
 * specific caller.
 *
 * @typedef {Readonly<Record<string, Readonly<Record<string, string>>>>} TestIdCatalog
 */

/**
 * @param {object} obj
 * @returns {object}
 */
function deepFreeze(obj) {
  for (const value of Object.values(obj)) {
    if (value && typeof value === "object") {
      deepFreeze(value);
    }
  }
  return Object.freeze(obj);
}

/** @type {TestIdCatalog} */
export const TestId = deepFreeze(catalog);
